import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePhones } from '../../api/hooks'
import { useAgentLogs, useAgentLogsLatest } from '../../api/agent'
import { hasLiveHistoryGap, mergeChronological } from '../../lib/agentLogFormat'
import { LogConsole } from '../../components/agentLogs/LogConsole'
import { SearchableSelect } from '../../components/SearchableSelect'

const LIVE_INTERVAL_MS = 5_000
// One page of logs — the row limit both the history and live queries request.
const PAGE_SIZE = 200

/** Admin console for per-phone agent logs. No phone selected → nothing is
 * fetched (L3). Live toggle polls; search/level filter client-side. */
export function AgentLogsPage() {
  const { data: phones } = usePhones()
  const [phoneId, setPhoneId] = useState<string | null>(null)
  const [level, setLevel] = useState('')
  const [search, setSearch] = useState('')
  const [live, setLive] = useState(false)

  // History pages ("Load older") never poll. Live mode polls ONLY the newest
  // page (useAgentLogsLatest) instead of re-fetching every loaded keyset page
  // each tick. The two are merged + deduped by id (live wins) into chronological
  // order.
  const query = useAgentLogs(phoneId, { level: level || undefined, limit: PAGE_SIZE })
  const liveQuery = useAgentLogsLatest(
    phoneId,
    { level: level || undefined, limit: PAGE_SIZE },
    { enabled: live, refetchInterval: live ? LIVE_INTERVAL_MS : false },
  )

  // Dropping the previous phone's cached log queries on switch prevents its
  // stale (possibly retention-purged) rows from resurfacing as current when the
  // operator switches back within React Query's gcTime (5 min) — the disabled
  // live query would otherwise serve those ghosts before the refetch lands.
  const qc = useQueryClient()
  const prevPhoneId = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevPhoneId.current
    if (prev && prev !== phoneId) {
      qc.removeQueries({ queryKey: ['agent-logs-latest', prev] })
      qc.removeQueries({ queryKey: ['agent-logs', prev] })
    }
    prevPhoneId.current = phoneId
  }, [phoneId, qc])

  // Bridge the live-vs-history gap: when a burst lands more rows than the live
  // window (one page) holds since history page 0 was last fetched, the rows
  // between the two windows belong to neither and stay invisible until the
  // history head advances. hasLiveHistoryGap detects that disjoint state (live's
  // oldest still newer than history's newest); only then do we refetch to close
  // it. The check is O(1) and false in steady state, so — unlike a "full live
  // page" gate — it does NOT re-fetch every loaded keyset page on each poll tick.
  // liveUpdatedAt stays in deps so a sustained burst re-checks each poll
  // resolution even when the array identities are unchanged.
  const historyPage0 = query.data?.pages[0]?.items
  const liveItems = liveQuery.data?.items
  const liveUpdatedAt = liveQuery.dataUpdatedAt
  const refetchHistory = query.refetch
  useEffect(() => {
    if (live && hasLiveHistoryGap(historyPage0, liveItems)) void refetchHistory()
  }, [live, historyPage0, liveItems, liveUpdatedAt, refetchHistory])

  // Memoized so its identity is stable when content is unchanged — otherwise a
  // fresh array every render (search keystroke, level toggle, 5s poll with no
  // new rows) would re-fire LogConsole's scroll effect and re-snap the view.
  const chronological = useMemo(
    () => mergeChronological(query.data?.pages, liveQuery.data?.items, search),
    [query.data?.pages, liveQuery.data, search],
  )

  const selectCls =
    'rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900'

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchableSelect
          options={phones?.map((p) => ({ value: p.id, label: p.name, sublabel: p.sim })) ?? []}
          value={phoneId ?? ''}
          onChange={(v) => setPhoneId(v || null)}
          allLabel="Select a phone…"
          searchPlaceholder="Поиск по имени или SIM"
          testId="phone-filter"
        />
        <select value={level} onChange={(e) => setLevel(e.target.value)} className={selectCls}>
          <option value="">All levels</option>
          <option value="I">Info</option>
          <option value="W">Warn</option>
          <option value="E">Error</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className={selectCls}
        />
        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
          Live
        </label>
        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={!phoneId}
          className={`${selectCls} disabled:opacity-50`}
        >
          Refresh
        </button>
      </div>

      {!phoneId ? (
        <div className="flex flex-1 items-center justify-center text-gray-500">
          Select a phone to view its logs.
        </div>
      ) : (
        <LogConsole
          // Remount on phone switch so scroll position, auto-stick, and the
          // "Load older" anchor reset. Without this the single instance carries
          // phone A's scrolled-up state into phone B, which then stops
          // auto-following new lines until the admin scrolls back to the bottom.
          key={phoneId}
          logs={chronological}
          hasOlder={query.hasNextPage ?? false}
          loadingOlder={query.isFetchingNextPage}
          onLoadOlder={() => query.fetchNextPage()}
        />
      )}
    </div>
  )
}
